import React, { useCallback, useMemo } from "react"
import classNames from "classnames"

/** Leading space for native select dropdown labels (CSS padding on <option> is unreliable). */
const OPTION_LABEL_PREFIX = "\u2003\u2003"

const DIVIDER_STYLE = {
  fontSize: "1px",
  backgroundColor: "currentColor",
  color: "inherit",
}

const isSameOptionValue = (value1, value2) => {
  if (value1 === undefined || value1 === null) {
    return value2 === undefined || value2 === null
  }
  return value1 === value2
}

const getSelectedOption = (options, value) => {
  for (const option of options) {
    if (!option.divider && isSameOptionValue(option.value, value)) {
      return option
    }
  }
  return undefined
}

const PaddedCountrySelect = ({
  value,
  onChange,
  options,
  disabled,
  readOnly,
  className,
  hideArrow: _hideArrow,
  ...rest
}) => {
  const paddedOptions = useMemo(
    () =>
      options.map(option =>
        option.divider
          ? option
          : { ...option, label: `${OPTION_LABEL_PREFIX}${option.label}` }
      ),
    [options]
  )

  const handleChange = useCallback(
    event => {
      const nextValue = event.target.value
      onChange(nextValue === "ZZ" ? undefined : nextValue)
    },
    [onChange]
  )

  return (
    <select
      {...rest}
      disabled={disabled || readOnly}
      readOnly={readOnly}
      value={value || "ZZ"}
      onChange={handleChange}
      className={classNames("PhoneInputCountrySelect", className)}
    >
      {paddedOptions.map(({ value: optionValue, label, divider }) => (
        <option
          key={divider ? "|" : optionValue || "ZZ"}
          value={divider ? "|" : optionValue || "ZZ"}
          disabled={Boolean(divider)}
          style={divider ? DIVIDER_STYLE : undefined}
        >
          {label}
        </option>
      ))}
    </select>
  )
}

const DefaultArrowComponent = () => (
  <div className="PhoneInputCountrySelectArrow" />
)

/**
 * Country selector for react-phone-number-input with padded dropdown labels.
 * Mirrors the library's CountrySelectWithIcon while fixing native option spacing.
 */
const PhoneCountrySelect = ({
  value,
  options,
  className,
  iconComponent: Icon,
  arrowComponent: Arrow = DefaultArrowComponent,
  hideArrow = false,
  ...rest
}) => {
  const selectedOption = useMemo(
    () => getSelectedOption(options, value),
    [options, value]
  )

  return (
    <div className="PhoneInputCountry">
      <PaddedCountrySelect
        {...rest}
        value={value}
        options={options}
        className={className}
        hideArrow={hideArrow}
      />
      {selectedOption && Icon ? (
        <Icon
          aria-hidden
          country={value}
          label={selectedOption.label}
        />
      ) : null}
      {hideArrow ? null : <Arrow />}
    </div>
  )
}

export default PhoneCountrySelect
